import { memo, useCallback, type KeyboardEvent } from 'react';
import { Bell } from 'lucide-react';
import { cn } from '@clarix/ui';
import { timeAgo } from '@clarix/utils';
import type { FeedCategoryMap, FeedNotification } from './types';

interface NotificationFeedRowProps {
  notification: FeedNotification;
  categories: FeedCategoryMap;
  onMarkRead?: (id: string) => void;
  onView?: (notification: FeedNotification) => void;
  isNew?: boolean;
  /** Indented, smaller variant used inside a cluster. */
  compact?: boolean;
}

/**
 * ONE feed row for every app. Ported from the projects `NotificationItem` —
 * this file is the reference for the row's geometry, so do not restyle it in
 * an app; every consumer inherits changes made here.
 *
 * Geometry (do not drift): gap-2.5 · px-4 py-2 · 30×30 rounded-lg tile ·
 * title text-lg/1.45 · body text-sm · meta text-xs · 1×1 unread dot.
 */
export const NotificationFeedRow = memo(function NotificationFeedRow({
  notification: n,
  categories,
  onMarkRead,
  onView,
  isNew,
  compact,
}: NotificationFeedRowProps) {
  // Per-notification appearance wins, then the category's, then a neutral bell.
  const config = categories[n.category];
  const Icon = n.appearance?.icon ?? config?.icon ?? Bell;
  const tileBg = n.appearance?.bg ?? config?.bg ?? 'bg-muted';
  const tileColor = n.appearance?.color ?? config?.color ?? 'text-muted-foreground';

  const handleClick = useCallback(() => {
    if (!n.isRead) onMarkRead?.(n.id);
    onView?.(n);
  }, [n, onMarkRead, onView]);

  const handleKeyDown = useCallback(
    (e: KeyboardEvent<HTMLDivElement>) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        handleClick();
      }
    },
    [handleClick],
  );

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      className={cn(
        'relative flex cursor-pointer gap-2.5 transition-colors duration-75',
        compact ? 'py-2 pl-14 pr-4' : 'px-4 py-2',
        'hover:bg-accent',
        !n.isRead && 'bg-primary-light/20',
        isNew && 'notif-glow-in',
      )}
    >
      {/* Unread dot */}
      {!n.isRead && (
        <span className="bg-primary absolute left-[7px] top-1/2 h-1 w-1 -translate-y-1/2 rounded-full" />
      )}

      {/* Category tile */}
      <div
        className={cn(
          'flex flex-shrink-0 items-center justify-center',
          compact ? 'h-6 w-6 rounded-md' : 'h-[30px] w-[30px] rounded-lg',
          tileBg,
        )}
      >
        <Icon size={compact ? 11 : 14} strokeWidth={1.5} className={tileColor} />
      </div>

      {/* Body */}
      <div className="min-w-0 flex-1">
        <p className="text-muted-foreground text-lg leading-[1.45]">
          {n.actorName && <span className="text-foreground font-medium">{n.actorName} </span>}
          {n.title}
        </p>
        {(n.body || n.entityName) && (
          <p className="text-muted-foreground mt-px truncate text-sm">
            {n.body}
            {n.body && n.entityName && ' · '}
            {n.entityName && <span className="text-foreground font-medium">{n.entityName}</span>}
          </p>
        )}
        <div className="mt-0.5 flex items-center gap-[5px]">
          {n.tag && (
            <>
              <span className="rounded-[3px] bg-[rgb(var(--tone-brand-bg))] px-[5px] py-px text-xxs font-medium text-[rgb(var(--tone-brand-fg))]">
                {n.tag}
              </span>
              <span className="bg-muted-foreground h-0.5 w-0.5 rounded-full" />
            </>
          )}
          <span className="text-muted-foreground text-xs">{timeAgo(n.createdAt)}</span>
        </div>
      </div>
    </div>
  );
});
